import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { gql } from '@soundxyz/gql-string';
import { ReleaseCard } from '@components/SongCards/ReleasesSongCard';
import type { ArtistReleasesFilter, FragmentType } from '@graphql/reactQuery';
import {
  AllArtistAppearsOnReleasesDocument,
  ArtistAppearsOnSectionArtistInfoFragmentDoc,
  ArtistMintedReleasesAuthorFilterOption,
  ArtistMintedReleasesCreditSplitFilterOption,
  getFragment,
  ReleaseAlbumRevealFilterOption,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { formatProfileHeader } from '@utils/helpers';
import { CardsContainer } from './CardsContainer';
import { SectionHeader } from './SectionHeader';
import { Subheader } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query AllArtistAppearsOnReleases(
    $artistId: UUID!
    $pagination: CursorConnectionArgs!
    $filter: ArtistReleasesFilter!
  ) {
    artist(id: $artistId) {
      id
      user {
        id
      }
      numReleases(filter: $filter)
      releases(pagination: $pagination, filter: $filter) {
        edges {
          node {
            id
            ...ReleasesCard
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }

  fragment ArtistAppearsOnSectionArtistInfo on Artist {
    id
    webappUri
  }
`);

export const ArtistAppearsOnSection = ({
  artist,
}: {
  artist: FragmentType<ArtistAppearsOnSectionArtistInfoFragmentDoc>;
}) => {
  const { id: artistId, webappUri } = getFragment(
    ArtistAppearsOnSectionArtistInfoFragmentDoc,
    artist,
  );

  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.1,
  });

  const filter: ArtistReleasesFilter = {
    releaseAuthor: ArtistMintedReleasesAuthorFilterOption.OnlyAppearsOn,
    creditSplit: ArtistMintedReleasesCreditSplitFilterOption.All,
    releaseAlbumRevealStatus: ReleaseAlbumRevealFilterOption.OnlyNotRevealedAlbums,
  };

  const {
    orderedList: releases,
    isLoadingNewPage: loading,
    loadMoreNextPage: loadMore,
    hasNextPage = false,
    latestData,
  } = useInfiniteQuery(AllArtistAppearsOnReleasesDocument, {
    getNextPageParam({ data }) {
      return (
        data.artist?.releases.pageInfo.hasNextPage && {
          after: data.artist.releases.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {
      artistId,
      filter,
    },
    variables({ pageParam }) {
      return {
        artistId,
        pagination: {
          first: 20,
          after: pageParam?.after || null,
        },
        filter,
      };
    },
    list({ artist }) {
      return artist?.releases.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  useEffect(() => {
    if (isAtBottom && hasNextPage) {
      loadMore();
    }
  }, [isAtBottom, loadMore, hasNextPage]);

  const numReleases = latestData?.data.artist?.numReleases ?? 0;

  const subheader = formatProfileHeader({ count: numReleases, text: 'Releases' });

  return (
    <>
      <SectionHeader header="Appears On" userWebappUri={webappUri} />
      {loading ? (
        <div className="mt-2 h-6 w-32 animate-pulse rounded-md bg-neutral200" />
      ) : (
        <Subheader>{subheader}</Subheader>
      )}
      <CardsContainer fullWidth={false} hasFilter={false}>
        {releases?.map(release => (
          <ReleaseCard
            key={release.id}
            data={release}
            playlist={{
              type: 'ARTIST',
              associationId: artistId,
            }}
          />
        ))}
        <div ref={bottomRef} />
      </CardsContainer>
    </>
  );
};
